import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import Icon from '../../../../components/icons'
import Text from '../../../../elements/Typo/Text'

import { ExperimentDetailsI, OptionI } from '../../../../_config/types'
import { trackEvent } from '../../../../_common/functions'
import Dropdown from '../../../../elements/Form/dropdown'

import CustomDatePicker from '../../../../components/CustomDatePicker'
import { EXPERIMENT_TYPES } from '../../../../_common/constant'

export interface KPIDropDownOptionI {
    label: string
    value: string
    kpi_type: string
    kpi_literal?: string
    kpi_unit?: string
}

interface Props {
    experiment: ExperimentDetailsI
    groupOptions: OptionI[]
    selectedGroup: OptionI | null
    onGroupChange: (e: OptionI) => void
    kpiOptions: KPIDropDownOptionI[]
    selectedKPI: KPIDropDownOptionI[]
    setShowAlertMsg: (b: boolean) => void
    onKpiChange: (e: any) => void
    shortView: boolean
    onDateChange: (e: any) => void
    selectedDateRange: any[]
    toDownloadAsReport: boolean
}
const Filter: React.FC<Props> = ({ experiment, groupOptions, selectedGroup, onGroupChange, kpiOptions, selectedKPI, setShowAlertMsg, onKpiChange, shortView, onDateChange, selectedDateRange, toDownloadAsReport }) => {
    const { t } = useTranslation()
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false)
    const [dateBoundary, setDateBoundary] = useState<{
        minDate: Date
        maxDate: Date
    }>()
    //  console.log('fsdfs 1111444', experiment)

    const customKPIOptionLabel = (option: any) => (
        <div className="flex items-center justify-between gap-2">
            <span>{option.label}</span>
            {option.value === kpiOptions[0]?.value && <span className="badge badge-secondary  px-1 py-0 leading-none h-[0.8rem] text-[0.5rem] ml-auto">{t('P')}</span>}
        </div>
    )

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        //  console.log('fsdfs 4444', experiment)
        const experimentStartDate = experiment?.start_date
        const experimentEndDate = experiment?.end_date
        const is_partial_measurement: boolean = experiment?.is_partial_measurement || false
        const partial_measurement_run_date = experiment?.partial_measurement_run_date || null

        const minDate = moment(experimentStartDate).toDate()
        let maxDate = moment(experimentEndDate).toDate()
        if (is_partial_measurement && partial_measurement_run_date) {
            maxDate = moment(partial_measurement_run_date).toDate()
        }

        setDateBoundary({
            minDate,
            maxDate,
        })
    }, [experiment])

    useEffect(() => {
        setShowDateRangePicker(false)

        if (selectedKPI.length === 0) {
            selectedKPI = kpiOptions[0] ? [kpiOptions[0]] : []

            onKpiChange(selectedKPI[0])
        }
    }, [selectedKPI])

    const _handleDateRangeChange = (e: any) => {
        if (e?.length === 2 && e[0] && e[1]) {
            onDateChange(e)
            const startDate = moment(e[0]).format('YYYY-MM-DD')
            const endDate = moment(e[1]).format('YYYY-MM-DD')
            trackEvent('experiment_details_date_range_applied', { experiment_literal: experiment?.experiment_literal, start_date: startDate, end_date: endDate })

            if (moment(startDate).diff(moment(dateBoundary?.minDate).format('YYYY-MM-DD'), 'days') == 0 && moment(endDate).diff(moment(dateBoundary?.maxDate).format('YYYY-MM-DD'), 'days') == 0) {
                setShowAlertMsg(false)
            } else {
                setShowAlertMsg(true)
            }
        } else {
            trackEvent('experiment_details_date_range_cleared', { experiment_literal: experiment?.experiment_literal })
            onDateChange(null)
            setShowAlertMsg(false)
        }
    }

    const _renderLargeView = () => {
        if (toDownloadAsReport) {
            return (
                <div className="flex gap-2">
                    <div className="flex flex-col justify-end">
                        <Text className="items-center !text-[0.636rem]">{t('Select Test Group:')}</Text>
                        <Text className="items-center">{selectedGroup?.label}</Text>
                    </div>
                    <div className="flex flex-col justify-end">
                        <Text className="items-center !text-[0.636rem]">{t('Select KPI:')}</Text>
                        <Text className="items-center">{selectedKPI.length ? selectedKPI[0]?.label : ''}</Text>
                    </div>
                    {showDateRangePicker ? (
                        <div className="flex flex-col justify-end">
                            <Text className="items-center !text-[0.636rem]">{t('Measurement Period:')}</Text>
                            <Text className="items-center">{selectedDateRange.join(' - ')}</Text>
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            )
        } else {
            return (
                <div className="flex gap-8">
                    {experiment?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title && (
                        <div className="flex flex-col  justify-end">
                            <Text className="items-center !text-[0.636rem]">{t('Select Test Group:')}</Text>
                            <Dropdown
                                options={groupOptions}
                                value={selectedGroup}
                                onChange={(e: OptionI) => {
                                    onGroupChange(e)
                                    trackEvent('experiment_details_group_selected', { experiment_literal: experiment?.experiment_literal, group: e?.label })
                                }}
                                className="min-w-[12rem]"
                            ></Dropdown>
                        </div>
                    )}
                    <div className="flex flex-col  justify-end">
                        <Text className="items-center !text-[0.636rem]">{t('Select KPI:')}</Text>
                        <Dropdown
                            options={kpiOptions}
                            formatOptionLabel={customKPIOptionLabel}
                            value={selectedKPI}
                            onChange={(e: any) => {
                                onKpiChange(e)
                                trackEvent('experiment_details_kpi_selected', { experiment_literal: experiment?.experiment_literal, kpi: e?.label })
                            }}
                            className="min-w-[12rem]"
                        />
                    </div>
                    {showDateRangePicker ? (
                        <div className="flex flex-col justify-end">
                            <Text className="items-center !text-[0.636rem]">{t('Measurement Period:')}</Text>
                            <CustomDatePicker
                                size="sm"
                                data-testid="date-range-picker"
                                type="range"
                                onChange={_handleDateRangeChange}
                                value={selectedDateRange.length && selectedDateRange[0] && selectedDateRange[1] ? [selectedDateRange[0], selectedDateRange[1]] : null}
                                minDate={dateBoundary?.minDate}
                                maxDate={dateBoundary?.maxDate}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            )
        }
    }

    const _renderSmallView = () => {
        return (
            <div ref={dropdownRef} className="relative">
                <button type="button" onClick={() => setShowDropdown(true)} className="flex gap-[0.33rem] bg-[#FFF] rounded-[1.667rem] items-center" data-aos="zoom-in" data-aos-delay="200" data-testid="dbtn">
                    <div data-testid="share-button" aria-label={t('Filter')} className="h-[1.182rem] w-[1.182rem] rounded-[1.045rem] bg-primary flex items-center justify-center cursor-pointer">
                        <Icon name="filter" size={0.682}></Icon>
                    </div>
                    <Text size="xs" className="!text-[0.591rem] !font-semibold">
                        {t('Filter')}
                    </Text>
                </button>
                {showDropdown && (
                    <div className="absolute bg-white border px-2 py-2 rounded-lg shadow-1 z-10 flex flex-col gap-2" style={{ right: -4 }}>
                        {experiment?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title && (
                            <div className="flex flex-col gap-1">
                                <Text className="items-center !text-[0.636rem]">{t('Select Test Group:')}</Text>
                                <Dropdown options={groupOptions} value={selectedGroup} onChange={(e: OptionI) => onGroupChange(e)} className="min-w-[6rem]"></Dropdown>
                            </div>
                        )}
                        <div className="flex flex-col gap-1">
                            <Text className="items-center !text-[0.636rem]">{t('Select KPI:')}</Text>
                            <Dropdown
                                options={kpiOptions}
                                value={selectedKPI}
                                onChange={(e: any) => {
                                    onKpiChange(e)
                                }}
                                className="min-w-[7rem]"
                            />
                        </div>
                        {showDateRangePicker ? (
                            <div className="flex flex-col gap-1">
                                <Text className="items-center !text-[0.636rem]">{t('Measurement Period:')}</Text>
                                <CustomDatePicker
                                    size="sm"
                                    data-testid="date-range-picker"
                                    type="range"
                                    onChange={_handleDateRangeChange}
                                    value={selectedDateRange.length && selectedDateRange[0] && selectedDateRange[1] ? [selectedDateRange[0], selectedDateRange[1]] : null}
                                    minDate={dateBoundary?.minDate}
                                    maxDate={dateBoundary?.maxDate}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return <div>{shortView ? _renderSmallView() : _renderLargeView()}</div>
}
export default Filter
